// GLOBALS
import groupBy from 'just-group-by';
import { useNavigate } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { normalizePortalPath } from '../../utilities/normalizePortalPath';

// PATIENT DOCUMENT HANDLING
import usePatientDoc from '../../hooks/usePatientDoc';
import { PatientDoc, ScreenObject } from '@usacsmideo/types';
import { getValidScreenArray } from '../../data/navigation/getValidScreenArray';

// DISPLAY ELEMENTS
// import Ionicons from '@expo/vector-icons/Ionicons';
// import { Pressable, Text, View } from 'react-native';
import { COLORS } from '../../style/COLORS';

export default function ApplicationOverview({ patientId }: { patientId: string }) {
  const patientDoc = usePatientDoc(patientId);
  const patientInfo = patientDoc?.data as PatientDoc;
  const navigate = useNavigate();
  if (!patientInfo || !patientInfo.navObj) return <>Loading...</>;

  // CONVERT THE NAVOBJ TO A VALIDATED, SORTED ARRAY OF SCREENS
  const navArray = getValidScreenArray(patientInfo.navObj, 'ApplicationOverview');

  // GROUP THE SCREENS BY SECTION
  const screensBySection = groupBy(navArray, function (screen: ScreenObject) {
    return screen.section;
  });

  // GET THE SECTION NAMES
  const sections = Object.keys(screensBySection);

  // BUILD THE DISPLAYED (NUMBERED) SECTION LIST — EXCLUDE HOME/ONBOARDING SO STEP
  // NUMBERS ARE CONTIGUOUS (1..N) OVER ONLY THE SECTIONS ACTUALLY RENDERED.
  const displayedSections = sections.filter((section) => !['Home', 'Onboarding'].includes(section));

  // USE THE SECTION NAMES TO SORT THE SCREENS OF EACH SECTION BY BASE ORDER
  sections.forEach((section) => {
    screensBySection[section as keyof typeof screensBySection].sort(
      (a: ScreenObject, b: ScreenObject) => a.baseOrder - b.baseOrder,
    );
  });

  return (
    <div className="flex w-full flex-col place-content-center place-items-center place-self-center">
      <p
        style={{
          fontSize: 28,
          fontWeight: 'bold',
          color: COLORS.onboarding.dark,
          marginBottom: 5,
        }}
      >
        Application Overview
      </p>
      {displayedSections.map((section, index) => (
          <button
            key={section}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigate({
                to: normalizePortalPath(screensBySection[section as keyof typeof screensBySection][0].portalPath) as FileRouteTypes['to'],
              });
            }}
            disabled={screensBySection[section as keyof typeof screensBySection][0].disabled ? true : false}
            className="w-full"
          >
            <div
              style={{
                borderRadius: 10,
                padding: 10,
                marginBottom: 10,
                flexDirection: 'row',
                justifyContent: 'space-between',
                // alignItems: 'center',
                backgroundColor: screensBySection[section as keyof typeof screensBySection][0].inactiveBackgroundColor,
              }}
              className="flex w-full flex-row items-center"
            >
              <div style={{ flexDirection: 'row' }} className="w-8/12">
                {/* <Ionicons
                  name={screensBySection[section as keyof typeof screensBySection][0].icon ?? undefined}
                  size={24}
                  color={screensBySection[section as keyof typeof screensBySection][0].disabled ? '#B5C1CE' : 'white'}
                /> */}
                <p
                  style={{
                    fontSize: 20,
                    fontWeight: 'bold',
                    color: screensBySection[section as keyof typeof screensBySection][0].disabled ? '#B5C1CE' : 'white',
                  }}
                  className="ml-4 text-left"
                >
                  {`${index + 1}. ${section as keyof typeof screensBySection}`}
                </p>
              </div>
              <div>
                {screensBySection[section as keyof typeof screensBySection][0].disabled ? (
                  <p
                    style={{
                      fontSize: 14,
                      fontStyle: 'italic',
                      color: screensBySection[section as keyof typeof screensBySection][0].disabled
                        ? '#B5C1CE'
                        : 'white',
                    }}
                  >
                    not ready
                  </p>
                ) : (
                  <div className="flex flex-row items-center">
                    <p
                      style={{
                        fontSize: 20,
                        fontWeight: 'bold',
                        color: screensBySection[section as keyof typeof screensBySection][0].disabled
                          ? '#B5C1CE'
                          : 'white',
                      }}
                    >
                      {screensBySection[section as keyof typeof screensBySection]
                        .filter((screen) => screen.disabled === false) // DON'T COUNT PREVIOUSLY COMPLETED SCREENS THAT ARE NOW DISABLED
                        .filter(
                          (screen) => screen.id.includes('Index') === false, // DON'T COUNT COMPLETED INDEX SCREENS
                        )
                        .filter(
                          (screen) => screen.id.includes('thanks') === false, // DON'T COUNT THANKS SCREEN IN THE TOTAL FOR ITS SECTION
                        )
                        .reduce((acc: number, screen: ScreenObject) => acc + (screen.completed ? 1 : 0), 0)}
                    </p>
                    <p
                      style={{
                        fontSize: 22,
                        fontWeight: 'bold',
                        color: screensBySection[section as keyof typeof screensBySection][0].disabled
                          ? '#B5C1CE'
                          : 'white',
                      }}
                    >
                      {`/${
                        screensBySection[section as keyof typeof screensBySection]
                          .filter((screen) => screen.disabled === false)
                          .filter(
                            (screen) => screen.id.includes('thanks') === false, // DON'T COUNT THANKS SCREEN IN THE TOTAL FOR ITS SECTION
                          )
                          .filter(
                            (screen) => screen.id.includes('Index') === false, // DON'T INCLUDE INDEX SCREENS IN THE TOTAL
                          ).length
                      }`}
                    </p>
                    {/* <Ionicons
                      name="chevron-forward"
                      size={24}
                      style={{ marginLeft: 10 }}
                      color={
                        screensBySection[section as keyof typeof screensBySection][0].disabled ? '#B5C1CE' : 'white'
                      }
                    /> */}
                  </div>
                )}
              </div>
            </div>
          </button>
      ))}

      {/* MY MIDEO FAMILY — static tile (CU-86aj2078m), NOT navObj-driven.
          The family hub is a standing persona surface (like /sessions), not an
          onboarding step, so it lives here as a fixed tile rather than a navObj
          screen. Mirrors the old "Your MIDEO Family" section palette. */}
      <button
        style={{ cursor: 'pointer' }}
        onClick={() => navigate({ to: '/myMideoFamily' })}
        className="w-full"
      >
        <div
          style={{
            borderRadius: 10,
            padding: 10,
            marginBottom: 10,
            flexDirection: 'row',
            justifyContent: 'space-between',
            backgroundColor: '#730D0E',
          }}
          className="flex w-full flex-row items-center"
        >
          <div style={{ flexDirection: 'row' }} className="w-8/12">
            <p
              style={{ fontSize: 20, fontWeight: 'bold', color: 'white' }}
              className="ml-4 text-left"
            >
              My MIDEO Family
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}
